import { useEffect, useState } from 'react';
import { lazy, Suspense } from 'react';
import { ACCESS_TOKEN_KEY, INITIAL_LD_KEY } from './core/constants';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import {
  useAppDispatch,
  useAppSelector,
  useAuth,
  useScrollTopOnRouteChange,
} from './hooks';
import {
  getAmazonConnectCredentials,
  getUser,
  getUserCompanies,
  postVisit,
} from './core/api';
import {
  activeCompanySelector,
  companiesSelector,
  userSelector,
} from './core/store/user/userSlice';
import { UserFlagEnum } from './core/types';
import { Navigate, Route, RouteProps, Routes } from 'react-router';
import { Loader } from './components/Loader/Loader';
import { PublicRoutesEnum, RolesEnum } from './core/enums';
import Notifications from './components/Notifications/Notifications';
import BookMeetingModal from './components/BookMeetingModal/BookMeetingModal';
import CreatePassword from './pages/CreatePassword/CreatePassword';
import DataProcessingAgreement from './pages/DataProcessingAgreement/DataProcessingAgreement';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import Login from './pages/Login/Login';
import OSSSelfService from './pages/OSS/OSSSelfService';
import Page404 from './pages/Page404/Page404';
import AmazonConnect from './pages/AmazonConnect/AmazonConnect';
import RegisterUser from './pages/RegisterUser/RegisterUser';
import TermsOfService from './pages/TermsOfService/TermsOfService';
import Register from './pages/Register/Register';

// Lazy load route components for code splitting
const TaxAdvisorRoutes = lazy(
  () => import('./pages/TaxAdvisor/TaxAdvisorRoutes')
);
const SellerRoutes = lazy(() => import('./pages/Seller/SellerRoutes'));
const SetupCheckRoutes = lazy(
  () => import('./pages/SetupCheck/SetupCheckRoutes')
);
const JtlTaxSettingsCheckRoutes = lazy(
  () => import('./pages/JtlTaxSettingsCheck/JtlTaxSettingsCheckRoutes')
);

const AmazonConnexSellerRoutes = lazy(
  () => import('./pages/AmazonConnexSeller/AmazonConnexSellerRoutes')
);
import { RegisterTaxAdvisorWithInvitation } from './pages/RegisterTaxAdvisorWithInvitation';
import { getCognitoUser } from './core/cognito';
import RegisterAmazonConnex from './pages/Register/RegisterAmazonConnex';

// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

const docsRoutes = [
  {
    element: <TermsOfService />,
    path: PublicRoutesEnum.TermsOfService,
  },
  {
    element: <DataProcessingAgreement />,
    path: PublicRoutesEnum.DataProcessingAgreement,
  },
];

const userRoutes: {
  [key: string]: RouteProps[];
} = {
  [RolesEnum.SELLER]: [
    {
      element: (
        <Suspense fallback={<Loader />}>
          <SellerRoutes />
        </Suspense>
      ),
      path: '/*',
    },
  ],
  [RolesEnum.SETUP_CHECK]: [
    {
      element: (
        <Suspense fallback={<Loader />}>
          <SetupCheckRoutes />
        </Suspense>
      ),
      path: '/*',
    },
  ],
  [RolesEnum.JTL_TAX_SETTINGS_CHECK]: [
    {
      element: (
        <Suspense fallback={<Loader />}>
          <JtlTaxSettingsCheckRoutes />
        </Suspense>
      ),
      path: '/*',
    },
  ],
  [RolesEnum.TAX_ADVISOR]: [
    {
      element: (
        <Suspense fallback={<Loader />}>
          <TaxAdvisorRoutes />
        </Suspense>
      ),
      path: '/*',
    },
  ],
  [RolesEnum.OSS]: [
    {
      element: <OSSSelfService />,
      path: '/*',
    },
  ],
  [RolesEnum.AMAZON_CONNEX_SELLER]: [
    {
      element: (
        <Suspense fallback={<Loader />}>
          <AmazonConnexSellerRoutes />
        </Suspense>
      ),
      path: '/*',
    },
  ],
  [RolesEnum.AGGREGATOR]: [],
};

const App = () => {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const { authUser } = useAuth();
  const user = useAppSelector(userSelector);
  const activeCompany = useAppSelector(activeCompanySelector);
  const companies = useAppSelector(companiesSelector);
  const { fibu738AmazonConnex } = useFlags();

  const ldClient = useLDClient();

  const publicRoutes = [
    {
      element: <Login />,
      path: '/',
    },
    {
      element: <ForgotPassword />,
      path: PublicRoutesEnum.ForgotPassword,
    },
    {
      element: <CreatePassword />,
      path: PublicRoutesEnum.CreatePassword,
    },
    {
      element: <RegisterUser role={RolesEnum.SELLER} />,
      path: PublicRoutesEnum.RegisterCustomer,
    },
    {
      element: <Register />,
      path: PublicRoutesEnum.Register,
    },
    {
      element: (
        <Register role={RolesEnum.OSS} flag={UserFlagEnum.oss_self_service} />
      ),
      path: PublicRoutesEnum.RegisterOSS,
    },

    {
      element: <RegisterAmazonConnex role={RolesEnum.AMAZON_CONNEX_SELLER} />,
      path: PublicRoutesEnum.RegisterAmazonConnex,
    },
    {
      element: <RegisterTaxAdvisorWithInvitation />,
      path: PublicRoutesEnum.RegisterTaxAdvisorWithInvitation,
    },
    {
      element: <Navigate to='/' />,
      path: '*',
    },
  ];

  useEffect(() => {
    if (import.meta.env.VITE_APP_ENV !== 'test') {
      if (user) {
        ldClient?.identify({
          kind: 'user',
          key: user.Email,
          company_id: activeCompany?.company_id || user.CompanyUuid,
          company_name: activeCompany?.name || user.CompanyName,
          has_de_company: Number(
            companies.some((c) => c.Country?.short_name === 'DE')
          ),
        });
      } else {
        ldClient?.identify({
          key: INITIAL_LD_KEY,
        });
      }
    }
    // eslint-disable-next-line
  }, [user, activeCompany]);

  useScrollTopOnRouteChange();

  useEffect(() => {
    if (localStorage.getItem(ACCESS_TOKEN_KEY)) {
      Promise.all([
        getUser(),
        getAmazonConnectCredentials(),
        getUserCompanies(),
        getCognitoUser(),
      ])
        .then((responses) => {
          authUser(responses);
          postVisit();
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [authUser, dispatch]);

  useEffect(() => {
    if (
      import.meta.env.VITE_APP_ENV &&
      import.meta.env.VITE_APP_ENV !== 'prod'
    ) {
      const favicon = document.getElementById('favicon')!;
      const appleFavicon = document.getElementById('apple-favicon')!;
      if (favicon) {
        (favicon as HTMLLinkElement).href = '/logo192-env.png';
      }
      if (appleFavicon) {
        (appleFavicon as HTMLLinkElement).href = '/logo192-env.png';
      }
    }
  }, []);

  useEffect(() => {
    if (
      import.meta.env.VITE_APP_ENV !== 'prod' &&
      import.meta.env.VITE_APP_ENV !== 'test'
    ) {
      const head = document.head;

      const script = document.createElement('script');
      script.setAttribute('id', 'CookieBot');
      script.setAttribute('src', 'https://consent.cookiebot.com/uc.js');
      script.setAttribute('data-cbid', '5ba2f28f-df61-4e9f-bc9f-e78e9f8d2bb0');
      script.setAttribute('type', 'text/javascript');
      script.setAttribute('async', 'true');
      script.setAttribute('data-culture', i18n.language.toUpperCase());
      head.insertBefore(script, head.firstChild);
    }
    // eslint-disable-next-line
  }, []);

  const routes = [
    {
      element: <Page404 />,
      path: PublicRoutesEnum.Page404,
    },
    {
      element: <AmazonConnect />,
      path: PublicRoutesEnum.AmazonConnect,
    },
    ...docsRoutes,
    ...(user ? userRoutes[user.Role] : publicRoutes),
  ];

  if (isLoading) return <Loader />;

  return (
    <>
      <Routes>
        {routes
          .filter(
            (r) =>
              r.path !== PublicRoutesEnum.RegisterAmazonConnex ||
              fibu738AmazonConnex
          )
          .map((r) => (
            <Route element={r.element} path={r.path} key={r.path} />
          ))}
        <Route path='*' element={<Navigate to={PublicRoutesEnum.Page404} />} />
      </Routes>
      <Notifications />
      <BookMeetingModal />
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </>
  );
};

export default App;
